import React, { useMemo, useState } from "react";
import StorePage from "./pages/StorePage";
import CartPage from "./pages/CartPage";
import CheckoutPage from "./pages/CheckoutPage";
import CheckoutModal from "./components/CheckoutModal";
import type { CartItem } from "./types/store";
import { hasSessionCheckoutParam, upsertCartItem } from "./utils/cartHelpers";

type View = "store" | "cart";

const App: React.FC = () => {
  const isSessionCheckout = useMemo(() => hasSessionCheckoutParam(), []);
  const [view, setView] = useState<View>("store");
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  const subtotal = useMemo(
    () => cartItems.reduce((sum, item) => sum + item.unitPrice * item.quantity, 0),
    [cartItems]
  );
  const vat = subtotal * 0.19;
  const total = subtotal + vat;

  const checkoutMerchant = useMemo(() => {
    if (cartItems.length === 0) return { name: "Estia Payments" };
    const merchantKeys = Array.from(
      new Set(cartItems.map((item) => item.estiaMerchantId || `name:${item.merchantName}`))
    );
    if (merchantKeys.length === 1) {
      return { name: cartItems[0].merchantName };
    }
    return { name: "Multi-merchant cart" };
  }, [cartItems]);

  if (isSessionCheckout) {
    return <CheckoutPage />;
  }

  return (
    <>
      {view === "store" && (
        <StorePage
          cartCount={cartItems.reduce((sum, item) => sum + item.quantity, 0)}
          onOpenCart={() => setView("cart")}
          onAddToCart={(item) => setCartItems((prev) => upsertCartItem(prev, item))}
          getItemQuantity={(key) => cartItems.find((item) => item.key === key)?.quantity || 0}
        />
      )}
      {view === "cart" && (
        <CartPage
          items={cartItems}
          onBackToStore={() => setView("store")}
          onIncrease={(key) =>
            setCartItems((prev) =>
              prev.map((item) =>
                item.key === key ? { ...item, quantity: item.quantity + 1 } : item
              )
            )
          }
          onDecrease={(key) =>
            setCartItems((prev) =>
              prev.flatMap((item) => {
                if (item.key !== key) return item;
                if (item.quantity <= 1) return [];
                return { ...item, quantity: item.quantity - 1 };
              })
            )
          }
          onRemove={(key) => setCartItems((prev) => prev.filter((item) => item.key !== key))}
          onCheckout={() => setCheckoutOpen(true)}
        />
      )}
      <CheckoutModal
        open={checkoutOpen}
        amount={Number(total.toFixed(2))}
        merchantName={checkoutMerchant.name}
        onClose={() => setCheckoutOpen(false)}
      />
    </>
  );
};

export default App;
